/*
 * An organization's brand colours (ADR 0007) and the contrast they must keep where the interface
 * uses them. Pure functions: the operator tool refuses a pair of colours that fails a rule, warns
 * about one that fails a design check, and the web works out from the pair the text and hover
 * colours it shows, with the same arithmetic.
 *
 * Contrast is WCAG 2.x's: the relative luminance of each colour, and (L1 + 0.05) / (L2 + 0.05).
 */

/** A colour as it is stored: `#rrggbb`, lower case (organizations_brand_*_check). */
export const HEX_COLOR = /^#[0-9a-f]{6}$/

/**
 * A colour as `#rrggbb` in lower case, from `#rrggbb` or `#rgb`, in any case and with or without
 * `#` (in a shell, an unquoted `#` starts a comment). Anything else is null.
 */
export function normalizeHexColor(input: string): string | null {
  const raw = input.trim().replace(/^#/, '').toLowerCase()
  if (/^[0-9a-f]{6}$/.test(raw)) return `#${raw}`
  if (/^[0-9a-f]{3}$/.test(raw)) return `#${[...raw].map((c) => c + c).join('')}`
  return null
}

function channels(hex: string): [number, number, number] {
  if (!HEX_COLOR.test(hex)) throw new Error('not a #rrggbb colour')
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number]
}

const toHex = (values: readonly number[]): string =>
  `#${values
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`

/**
 * WCAG 2.x relative luminance of a `#rrggbb` colour, from 0 (black) to 1 (white). The threshold of
 * the sRGB curve is the standard's 0.04045 (WCAG 2.0 printed 0.03928; no 8-bit value tells them
 * apart).
 */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex).map((v) => {
    const c = v / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG 2.x contrast ratio of two colours, from 1 (the same) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ]
  return (light + 0.05) / (dark + 0.05)
}

/**
 * `base` with a share `weight` (0 to 1) of `other`, channel by channel in sRGB: what a browser
 * shows for `other` at that opacity over `base`.
 */
export function mixColors(base: string, other: string, weight: number): string {
  const a = channels(base)
  const b = channels(other)
  return toHex(a.map((v, i) => v * (1 - weight) + b[i]! * weight))
}

/**
 * The interface's own colours that the brand colours sit on or next to
 * (apps/web/src/styles/app.css): the page, the cards, the empty part of a progress bar (`line`)
 * and the two text colours a brand colour can take.
 */
export const UI_COLORS = {
  page: '#f6f6f6',
  surface: '#ffffff',
  line: '#e3e3e3',
  ink: '#111111',
  white: '#ffffff',
} as const

/** A pair of brand colours. They go together: the rules compare one with the other. */
export interface BrandColors {
  primary: string
  accent: string
}

/** Eklevo's colours, shown when an organization has none of its own (app.css). */
export const DEFAULT_BRAND: BrandColors = { primary: '#111111', accent: '#ffc857' }

/** How much of the text colour a button's background takes under the pointer. */
export const HOVER_MIX = 0.08
/** The opacity of the secondary lines of text on primary panels (`opacity-80`). */
export const MUTED_TEXT_OPACITY = 0.8

/** The text colour on a background: white or ink, whichever contrasts more with it. */
export function textColorOn(background: string): string {
  return contrastRatio(UI_COLORS.white, background) >= contrastRatio(UI_COLORS.ink, background)
    ? UI_COLORS.white
    : UI_COLORS.ink
}

/**
 * What the interface shows for a pair of brand colours: the custom properties of app.css
 * (`--color-primary`, `--color-primary-contrast`, `--color-primary-hover`, and the same for the
 * accent).
 */
export interface BrandTheme {
  primary: string
  primaryContrast: string
  primaryHover: string
  accent: string
  accentContrast: string
  accentHover: string
}

export function brandTheme(colors: BrandColors): BrandTheme {
  const primaryContrast = textColorOn(colors.primary)
  const accentContrast = textColorOn(colors.accent)
  return {
    primary: colors.primary,
    primaryContrast,
    primaryHover: mixColors(colors.primary, primaryContrast, HOVER_MIX),
    accent: colors.accent,
    accentContrast,
    accentHover: mixColors(colors.accent, accentContrast, HOVER_MIX),
  }
}

/** WCAG 2.x minimums: 1.4.3 for text, 1.4.11 for the parts of components and indicators. */
export const MIN_TEXT_CONTRAST = 4.5
export const MIN_UI_CONTRAST = 3
/**
 * Eklevo's design minimum for an accent button against what surrounds it. The same figure as
 * 1.4.11's, but not a WCAG requirement here: see BRAND_DESIGN_CHECKS.
 */
export const MIN_DESIGN_CONTRAST = 3

/**
 * The rules, one per way the interface uses a brand colour that WCAG asks a minimum of (the audit
 * of app.css and the components, ADR 0007 §5). The operator tool refuses a pair that fails one.
 *
 * - primary-text: the text of primary buttons, of the dark chips («En curso», «Eklevo»), of the
 *   person's messages to Noa and of the primary panels of the course page.
 * - primary-text-muted: the secondary lines of those panels, the same text at 80% opacity.
 * - primary-text-hover: the text of a primary button under the pointer.
 * - primary-ui: primary as the part of a component or indicator that shows it: the filled part of
 *   a progress bar next to its empty part (`line`), the console's checkbox and the buttons and
 *   chips on the white cards and on the page.
 * - accent-text, accent-text-hover: the text of accent buttons, at rest and under the pointer.
 */
export const BRAND_RULES = [
  'primary-text',
  'primary-text-muted',
  'primary-text-hover',
  'primary-ui',
  'accent-text',
  'accent-text-hover',
] as const
export type BrandRule = (typeof BRAND_RULES)[number]

/**
 * The design checks: the accent as the background of a button with text, against what surrounds
 * it. WCAG asks no minimum of it: a control with visible text needs no boundary of its hit area
 * (Understanding 1.4.11, «Boundaries»). Under 3:1 the button still reads by its text, but stands
 * out less, so the operator tool warns and saves the pair (ADR 0007 §5).
 *
 * - accent-ui-primary: an accent button on a primary panel (resume a course, start an assessment,
 *   load an external video).
 * - accent-ui-surface: the accent button of the current lesson, on its white card. The lesson is
 *   also marked by its «Activa» chip and its ink border, so the accent is never the only sign of
 *   it (WCAG 1.4.1).
 */
export const BRAND_DESIGN_CHECKS = ['accent-ui-primary', 'accent-ui-surface'] as const
export type BrandDesignCheck = (typeof BRAND_DESIGN_CHECKS)[number]

export interface ContrastCheck<Rule extends string = BrandRule> {
  rule: Rule
  /** The two colours measured: the brand colour, and the text or background it meets. */
  colors: [string, string]
  ratio: number
  minimum: number
  passed: boolean
}

function measure<Rule extends string>(
  rule: Rule,
  a: string,
  b: string,
  minimum: number,
): ContrastCheck<Rule> {
  const ratio = contrastRatio(a, b)
  return { rule, colors: [a, b], ratio, minimum, passed: ratio >= minimum }
}

/** Every rule for a pair of colours, in the order of BRAND_RULES. */
export function checkBrandContrast(colors: BrandColors): ContrastCheck[] {
  const theme = brandTheme(colors)
  const mutedText = mixColors(theme.primary, theme.primaryContrast, MUTED_TEXT_OPACITY)
  // The lightest-contrasting of the backgrounds primary meets.
  const [ui] = [UI_COLORS.surface, UI_COLORS.page, UI_COLORS.line]
    .map((background) => measure('primary-ui', theme.primary, background, MIN_UI_CONTRAST))
    .sort((x, y) => x.ratio - y.ratio)
  return [
    measure('primary-text', theme.primary, theme.primaryContrast, MIN_TEXT_CONTRAST),
    measure('primary-text-muted', theme.primary, mutedText, MIN_TEXT_CONTRAST),
    measure('primary-text-hover', theme.primaryHover, theme.primaryContrast, MIN_TEXT_CONTRAST),
    ui!,
    measure('accent-text', theme.accent, theme.accentContrast, MIN_TEXT_CONTRAST),
    measure('accent-text-hover', theme.accentHover, theme.accentContrast, MIN_TEXT_CONTRAST),
  ]
}

/** Every design check for a pair of colours, in the order of BRAND_DESIGN_CHECKS. */
export function checkBrandDesign(colors: BrandColors): ContrastCheck<BrandDesignCheck>[] {
  return [
    measure('accent-ui-primary', colors.accent, colors.primary, MIN_DESIGN_CONTRAST),
    measure('accent-ui-surface', colors.accent, UI_COLORS.surface, MIN_DESIGN_CONTRAST),
  ]
}

/** A ratio as the operator reads it: two decimals, never rounded up past a minimum. */
export function formatRatio(ratio: number): string {
  return `${(Math.floor(ratio * 100) / 100).toFixed(2)}:1`
}
